/*
 * Copyright (c) 2024. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import clsx from "clsx";
import useNavigation, {RouteName} from "utils/hooks/useNavigation";
import {AllowedStep, TIMESLOTS_ALLOWED_STEPS} from "store/booking/BookingReducer";
import {useTypedSelector} from "helpers/reducers";
import SellerBreadcrumb from "pages/Front/Booking/components/SellerBreadcrumb";
import FeatherIcon from "components/FeatherIcon";

interface BookingHeaderBreadcrumbProps {
    isFullWidth?: boolean;
}

const BookingHeaderBreadcrumb = (props: BookingHeaderBreadcrumbProps) => {
    const {t} = useTranslation();
    const bookingStep = useTypedSelector(state => state.bookingState.navigation.currentStep);
    const {isHomeBookingRoute, handleRouteNavigation, getCurrentPage, getCurrentStep, location, historyPush, stepsArray} = useNavigation();
    const [currentPage, setCurrentPage] = useState<RouteName>(RouteName.Main);
    const [currentStep, setCurrentStep] = useState<AllowedStep | null>(TIMESLOTS_ALLOWED_STEPS[0]);
    const itemRefs = useRef<(HTMLLIElement | null)[]>([]);
    const navigation = useNavigation();

    const isItemVisible = (item: HTMLElement, container: HTMLElement) => {
        const itemRect = item.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        return itemRect.top >= containerRect.top;
    };

    const scrollToLI = () => {
        const item = itemRefs?.current[getCurrentPage()];
        const navElement = item?.closest("nav");
        if (item && navElement) {
            item.scrollIntoView({behavior: "smooth", inline: "start", block: "center"});

            if (!isItemVisible(item, navElement)) {
                item.scrollIntoView({behavior: "smooth", inline: "start", block: "center"});
            }

            const scrollLeft = item.offsetLeft - (navElement.offsetWidth - item.offsetWidth) / 2;
            navElement.scrollTo({
                left: scrollLeft,
                behavior: "smooth",
                top: -50,
            });
        }
    };

    useEffect(() => {
        setCurrentStep(getCurrentStep(currentPage));
    }, [currentPage]);

    useEffect(() => {
        setCurrentPage(getCurrentPage());
        scrollToLI();
    }, [location.pathname]);

    if (!navigation.isBookingRoute() || navigation.isHomeBookingRoute()) {
        return <></>;
    }

    if (navigation.isBookingSellerRoute()) {
        return <SellerBreadcrumb />;
    }

    return (
        // <div className={"BookingHeaderBreadcrumb shadow-lg z-10 flex flex-col content-start w-full"}>
        <div className={"BookingHeaderBreadcrumb z-10 flex flex-col content-start w-full px-4 xl:px-0"}>
            {/*<div className={"flex items-center overflow-hidden w-auto"}>*/}
            <div className={"flex items-center overflow-hidden w-full max-w-full rounded-[10px]  relative bg-white mx-auto xl:max-w-7xl drop-shadow"}>
                <div className={"absolute left-0 inset-y-0 h-full w-5 bg-gradient-to-r from-white via-white/50 to-transparent z-20"} />
                <div className={"absolute right-0 inset-y-0 h-full w-5 bg-gradient-to-l from-white via-white/50 to-transparent z-20"} />
                {/*<nav aria-label="Breadcrumb" className="flex border-b border-gray-200 w-full bg-white overflow-x-auto scrollbar-x-hidden relative">*/}
                <nav aria-label="Breadcrumb" className="flex w-full bg-transparent overflow-x-auto scrollbar-x-hidden">
                    {/*<ol className={clsx("mx-auto flex w-full max-w-full", props.isFullWidth ? "" : "xl:max-w-7xl")} role="list">*/}
                    {/*<ol className={clsx("mx-auto flex w-full max-w-full font-medium rounded-[10px] bg-white", props.isFullWidth ? "" : "xl:max-w-7xl")} role="list">*/}
                    <ol
                        className={clsx(
                            "BookingHeaderBreadcrumbOrderedList mx-auto flex w-full max-w-full font-medium ",
                            props.isFullWidth ? "" : "xl:max-w-7xl"
                        )}
                        role="list"
                    >
                        {!isHomeBookingRoute() && (
                            // <li className={clsx("flex border-r border-[#D9D9D9]/50 pl-4 xl:pl-0 pr-4 lg:pr-4")}>
                            <li className={clsx("flex border-r border-[#D9D9D9]/50 px-4")}>
                                <div className={"flex items-center"}>
                                    <button
                                        // className="font-semibold text-black duration-75 hover:text-arbo-orange inline-flex items-center"
                                        className=" text-black duration-75 hover:text-arbo-orange inline-flex items-center"
                                        onClick={handleRouteNavigation}
                                    >
                                        {/*<ArrowUturnLeftIcon className={"w-4 h-4 flex-none mr-2"} />*/}
                                        <FeatherIcon name={"arrow-left"} className={"size-5 flex-none mr-2"} />
                                        <span>{t("Back")}</span>
                                    </button>
                                </div>
                            </li>
                        )}

                        {stepsArray.map((page, index) => {
                            const disabled = page.id >= bookingStep.id;

                            if (page.routeName === RouteName.Competitions && !navigation.getIsCompetitionItemsAvailable()) {
                                return;
                            }

                            return (
                                <li
                                    key={index}
                                    ref={el => (itemRefs.current[page.routeName] = el)}
                                    className={clsx(
                                        // "flex first:pl-4 pr-4 relative transition-colors duration-0",
                                        "flex px-2 md:px-3 lg:px-4 relative transition-colors duration-0",
                                        // disabled && page.routeName !== currentPage && "bg-gray-100",
                                        disabled && page.routeName !== currentPage && "",
                                        // "before:content-[''] before:absolute before:top-1/2 before:z-10 before:right-[-15px] lg:before:right-[-23px] before:transform-gpu before:-translate-y-1/2 before:w-0 before:h-0 before:border-t-[18px] lg:before:border-t-[23px] before:border-t-transparent before:border-b-[18px] lg:before:border-b-[23px] before:border-b-transparent before:border-l-[15px] lg:before:border-l-[23px]",
                                        // disabled && page.routeName !== currentPage ? "before:border-l-gray-100" : " before:border-l-white",
                                        !isHomeBookingRoute() && "[&:nth-child(2)]:pl-4",
                                        // ((disabled && page.routeName === currentPage) || (!disabled && page.routeName === currentPage)) &&
                                        // "!bg-gray-600 before:!border-l-gray-600"
                                        "min-h-[50px]",
                                        page.routeName === RouteName.Main && "border-r border-[#D9D9D9]/50 mr-2 md:mr-3 xl:mr-4 !pr-4"
                                    )}
                                >
                                    <div className="flex items-center">
                                        {/*{page.routeName !== RouteName.Main && (*/}
                                        {/*    <svg*/}
                                        {/*        aria-hidden="true"*/}
                                        {/*        className="h-[36px] lg:h-full w-4 lg:w-6 flex-shrink-0 text-gray-200 lg:text-gray-300"*/}
                                        {/*        fill="currentColor"*/}
                                        {/*        preserveAspectRatio="none"*/}
                                        {/*        viewBox="0 0 24 44"*/}
                                        {/*    >*/}
                                        {/*        <path d="M.293 0l22 22-22 22h1.414l22-22-22-22H.293z" />*/}
                                        {/*    </svg>*/}
                                        {/*)}*/}
                                        <button
                                            aria-current={page.routeName == currentPage ? "page" : undefined}
                                            className={clsx(
                                                // "text-sm leading-4",
                                                "leading-4 whitespace-nowrap",
                                                // page.routeName !== RouteName.Main && "ml-4",
                                                // page.routeName == currentPage ? "font-semibold text-white" : "font-medium text-black",
                                                page.routeName == currentPage ? " text-arbo-red" : " text-black",
                                                // disabled && page.routeName !== currentPage && "!text-gray-500",
                                                disabled && page.routeName !== currentPage && "",
                                                !disabled && " hover:text-arbo-orange",
                                                "flex flex-row items-center"
                                            )}
                                            disabled={page.routeName === RouteName.Main ? false : disabled}
                                            onClick={() => historyPush(page.routeName)}
                                        >
                                            {page.routeName === RouteName.Main ? (
                                                <FeatherIcon
                                                    name={"home"}
                                                    className={clsx(
                                                        "size-6 flex-none",
                                                        page.routeName == currentPage ? "text-arbo-red" : "text-black hover:text-arbo-orange"
                                                    )}
                                                />
                                            ) : (
                                                // <HomeIcon
                                                //     aria-hidden="true"
                                                //     className={clsx(
                                                //         "h-5 w-5 flex-shrink-0",
                                                //         page.routeName == currentPage ? "text-white" : "text-black hover:text-arbo-orange"
                                                //     )}
                                                // />
                                                <>{t(page.label)}</>
                                            )}

                                            {page.routeName !== RouteName.Main && <FeatherIcon name={"chevrons-right"} className={"size-6 flex-none ml-2"} />}
                                        </button>
                                    </div>
                                </li>
                            );
                        })}
                    </ol>
                </nav>
            </div>
        </div>
    );
};

export default BookingHeaderBreadcrumb;
